import type { AppPublicRuntimeConfig } from '~/utils/runtimeConfig'
import { useTypedRuntimeConfig } from '~/utils/runtimeConfig'

/**
 * Cloudflare Image Resizing, delivered through the zone's `/cdn-cgi/image/`
 * endpoint (ADO 62975, plan Option A):
 *
 *     {CF_RESIZE_ZONE}/cdn-cgi/image/{params}/{source}
 *
 * The source is an absolute URL composed from `MEDIA_ASSET_URL` — the same
 * value `mediaAssetUrl` uses — so the media host has a single source of truth.
 * `CF_IMAGE_ORIGIN` carries only the library path base beneath it, rather than
 * repeating the hostname and letting the two drift.
 */

/**
 * Description of an image transform. Each optional field maps to one
 * Cloudflare Image Resizing option, serialized by `serializeParams` below; the
 * fields from `dpr` down are the ADO 61696 delivery settings.
 */
export type ImageTransform = {
  width?: number
  height?: number
  /**
   * `baseline` defers to the serializer's delivery baseline (85, since masters
   * are served untouched — 61696 §1.5) instead of pinning a number at every
   * call site. An explicit number still wins.
   */
  quality?: number | 'auto' | 'baseline'
  format?: 'auto'
  /** `scale-down` never upscales a master past its stored size (61696 §2.2). */
  fit?: 'cover' | 'crop' | 'scale-down'
  /** `auto` is saliency autocrop, preferred over editor pre-crops (61696 §1.7). */
  gravity?: 'center' | 'east' | 'auto'
  /** Capped at 2 — Cloudflare's maximum (61696 §2.3). */
  dpr?: 1 | 2
  /** Applied to the smallest derived sizes (61696 §2.6). */
  sharpen?: number
  /** Strips EXIF/GPS at delivery (61696 §1.4). */
  metadata?: 'copyright' | 'none'
}

/**
 * What `quality: 'baseline'` means. 85, because Cloudflare serves untouched
 * masters and delivery is the only lossy pass (ADO 61696 §1.5). It is also
 * Cloudflare's own default.
 */
const BASELINE_QUALITY = 85

/**
 * `fit` needs no translation — the descriptor was named after Cloudflare's
 * vocabulary. Kept as an explicit map so an added descriptor value fails to
 * compile here rather than emitting a param Cloudflare will ignore.
 */
const FIT_PARAMS: Record<NonNullable<ImageTransform['fit']>, string> = {
  cover: 'cover',
  crop: 'crop',
  'scale-down': 'scale-down'
}

/**
 * Cloudflare's own default `fit`, which is therefore never written out. Every
 * product descriptor carries `fit: 'scale-down'` to state the no-upscale
 * intent; naming it in the URL as well would give one image at one width a
 * second URL — a second cache key and billable transformation — for identical
 * pixels (ADO 63948).
 */
const DEFAULT_FIT: NonNullable<ImageTransform['fit']> = 'scale-down'

/**
 * Cloudflare's `gravity` accepts `auto`, `face`, a side, or coordinates — there
 * is no `center`, because centre is what it already does when gravity is unset.
 * `center` therefore maps to nothing rather than to a param Cloudflare would
 * reject, and the compass name `east` becomes the side name.
 */
const GRAVITY_PARAMS: Record<NonNullable<ImageTransform['gravity']>, string | undefined> = {
  center: undefined,
  east: 'right',
  auto: 'auto'
}

const requireValue = (value: string | undefined, key: string): string => {
  if (!value) {
    throw new Error(`${key} missing in environment`)
  }
  return value
}

const trimTrailingSlashes = (value: string): string => value.replace(/\/+$/, '')

const trimSurroundingSlashes = (value: string): string => value.replace(/^\/+|\/+$/g, '')

/**
 * Serializes a descriptor to Cloudflare's comma-separated option segment.
 *
 * Returns an empty string for a descriptor that expresses nothing, which
 * `deliver` uses to skip the transform prefix entirely.
 *
 * One independent `if` per optional descriptor field, each emitting one param
 * in canonical order; nothing nests and no branch depends on another. It is
 * deliberately left flat: splitting it into helpers would move the same
 * branches without removing a decision, and a reader checking the param order
 * would have to follow them. A new descriptor field adds one more branch here.
 */
const serializeParams = (transform: ImageTransform): string => {
  const params: string[] = []

  if (transform.width !== undefined) {
    params.push(`width=${transform.width}`)
  }
  if (transform.height !== undefined) {
    params.push(`height=${transform.height}`)
  }
  if (transform.quality !== undefined && transform.quality !== 'auto') {
    // `auto` is deliberately absent: Cloudflare accepts 1-100 or the perceptual
    // keywords (high / medium-high / medium-low / low) and has no automatic
    // mode. Omitting the param falls through to the platform default, which is
    // what `auto` asks for — let the platform choose — so this translates the
    // intent rather than dropping a requested value.
    const quality = transform.quality === 'baseline' ? BASELINE_QUALITY : transform.quality
    params.push(`quality=${quality}`)
  }
  if (transform.format !== undefined) {
    params.push(`format=${transform.format}`)
  }
  if (transform.fit !== undefined && transform.fit !== DEFAULT_FIT) {
    params.push(`fit=${FIT_PARAMS[transform.fit]}`)
  }
  if (transform.gravity !== undefined) {
    const gravity = GRAVITY_PARAMS[transform.gravity]
    if (gravity !== undefined) {
      params.push(`gravity=${gravity}`)
    }
  }
  if (transform.dpr !== undefined) {
    params.push(`dpr=${transform.dpr}`)
  }
  if (transform.sharpen !== undefined) {
    params.push(`sharpen=${transform.sharpen}`)
  }
  if (transform.metadata !== undefined) {
    params.push(`metadata=${transform.metadata}`)
  }

  return params.join(',')
}

/**
 * Composes the delivery URL, skipping the `/cdn-cgi/image/` prefix when the
 * descriptor expresses nothing.
 *
 * The skip is not a tidiness choice: an empty params segment leaves
 * `/cdn-cgi/image//<source>`, and that double slash 404s on the live zone —
 * measured 2026-09-08 against cdn.stampinup-media.com. An untransformed image
 * needs no transform anyway, so it is served straight from the origin.
 */
const deliver = (config: AppPublicRuntimeConfig, source: string, transform: ImageTransform): string => {
  const params = serializeParams(transform)
  if (!params) {
    return source
  }

  const zone = trimTrailingSlashes(requireValue(config.CF_RESIZE_ZONE, 'CF_RESIZE_ZONE'))

  return `${zone}/cdn-cgi/image/${params}/${source}`
}

const mediaHost = (config: AppPublicRuntimeConfig): string =>
  trimTrailingSlashes(requireValue(config.MEDIA_ASSET_URL, 'MEDIA_ASSET_URL'))

const IMAGE_EXTENSION = '.jpg'

const normalizeImageName = (imageName: string | number): string => {
  const image = typeof imageName === 'number' ? imageName.toString() : imageName
  return image.includes(IMAGE_EXTENSION) ? image : `${image}${IMAGE_EXTENSION}`
}

/**
 * Resolves the public runtime config for a call.
 *
 * A caller supplies `configuredConfig` precisely because it has no Nuxt context
 * to read from — `ProductDetailBase` captures the config during setup and uses
 * it inside a `watchEffect`, where composables are not safely callable. The
 * composable must not be touched on that path.
 *
 * It is the **whole public config**, not a single value, so every image setting
 * travels with it: an override missing one would build the PDP's preloads,
 * `og:image` and product JSON-LD from a different host than the rest of the
 * page (ADO 63189). Passing the object covers the media host, both Cloudflare
 * variables, and anything added later, rather than growing one parameter per
 * variable.
 */
const configFor = (configuredConfig?: AppPublicRuntimeConfig): AppPublicRuntimeConfig =>
  configuredConfig ?? useTypedRuntimeConfig().public

export const serializeImageUrl = (
  library: string,
  imageName: string | number,
  transform: ImageTransform,
  configuredConfig?: AppPublicRuntimeConfig
): string => {
  const config = configFor(configuredConfig)
  const origin = trimSurroundingSlashes(requireValue(config.CF_IMAGE_ORIGIN, 'CF_IMAGE_ORIGIN'))
  const source = `${mediaHost(config)}/${origin}/${library}/${normalizeImageName(imageName)}`

  return deliver(config, source, transform)
}

/**
 * Builds a URL for a media asset that carries a transform — the credit-card
 * icons and the lazy-load placeholders.
 *
 * `key` is the **full** object key, folder included
 * (`application_assets/visa_cc.png`, `loading_image.png`). No prefix is baked
 * in, because the key space has no single root: foldered keys
 * (`application_assets/…`) and bare root keys both exist (ADO 62973).
 * Passing the whole key also means the string at the call site *is* the stored
 * key, which is what makes a mis-cased reference reviewable. It resolves
 * against the media host verbatim — no library segment, no case folded
 * (ADO 62986) — and `CF_IMAGE_ORIGIN`, the product-library path base, is not
 * involved.
 *
 * Unlike `serializeImageUrl` this appends no extension. Every asset caller
 * resolves inside setup today, so the config override is accepted for symmetry
 * rather than out of need.
 *
 * A transform-free asset needs no serializer at all — see
 * `~/utils/format/mediaAssets`.
 */
export const serializeAssetUrl = (
  key: string,
  transform: ImageTransform,
  configuredConfig?: AppPublicRuntimeConfig
): string => {
  const config = configFor(configuredConfig)

  return deliver(config, `${mediaHost(config)}/${key}`, transform)
}

/** The placeholder key, at the bare delivery root (ADO 62973). */
const MISSING_IMAGE_KEY = 'missing_image.png'

/**
 * The width the placeholder is delivered at. The stored object is a 2000x2000
 * master, so it is always resized rather than served whole; 360 matches the
 * loading placeholder and lands around 2 KB.
 */
const MISSING_IMAGE_TRANSFORM: ImageTransform = { width: 360, format: 'auto' }

/**
 * The image shown when a product image does not exist.
 *
 * Resolved through the same serializer as every other URL rather than pinning
 * a host.
 *
 * Cloudflare has no delivery-time default-image substitution, and `/cdn-cgi/`
 * fires neither Snippets nor Workers routes (measured 2026-09-08), so no
 * server-side substitution is reachable — the swap happens in an `onerror`
 * handler instead (ADO 62991).
 */
export const missingImageUrl = (
  transform: ImageTransform = MISSING_IMAGE_TRANSFORM,
  configuredConfig?: AppPublicRuntimeConfig
): string => serializeAssetUrl(MISSING_IMAGE_KEY, transform, configuredConfig)
